'use client';

import React, { useMemo } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCheck, faCopy, faPencil } from '@fortawesome/pro-solid-svg-icons';
import useAuthorization from 'src/hooks/authorization/useAuthorization';
import useDirectReports from 'src/hooks/companies/managers/useDirectReports';
import { useAuth } from 'src/context/auth';
import { useSelector } from 'react-redux';
import { selectDiscoveryMapCurrentTerritory } from 'src/redux/slices/discoveryMapSlice';
import { useRouter } from 'next/navigation';

interface TerritoryPanelItemProps {
  territory: MedScout.Territory;
  isSelected: boolean;
  onSelect: (territory: MedScout.Territory) => void;
  onCopy: (territoryId: number) => void;
  onClose?: () => void;
}

const TerritoryPanelItem: React.FC<TerritoryPanelItemProps> = ({
  territory,
  isSelected,
  onSelect,
  onCopy,
  onClose,
}) => {
  const { user } = useAuth();
  const auth = useAuthorization();
  const isBoundToAssigned = auth?.isBoundedToTerritory();
  const router = useRouter();
  const { checkReport } = useDirectReports();

  const currentTerritory = useSelector(selectDiscoveryMapCurrentTerritory);

  const isMedScoutUser =
    user?.is_superuser || user?.permissions?.is_support_user;

  const canUserWrite = useMemo(() => {
    // Handle case when no territory is selected
    if (!currentTerritory) {
      return false;
    }

    // Determine if the territory is canonical if the user is bound to assigned territories
    const isCanonical = isBoundToAssigned && currentTerritory.is_canonical;

    // Check if user owns the territory
    const isOwnTerritory =
      currentTerritory.owner.id === user.id && !isCanonical;

    // Determine permission scope
    const scope = isOwnTerritory
      ? 'own'
      : checkReport(currentTerritory.owner.id)
      ? 'direct_report'
      : 'company';

    return auth?.canWrite('territory', scope);
  }, [
    currentTerritory,
    user.id,
    auth,
    checkReport,
    isBoundToAssigned,
  ]);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onCopy) {
      onCopy(territory.id);
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    router.push(`/territory-manager/edit/${territory.id}`);
    if (onClose) {
      onClose();
    }
  };

  // Don't show edit/copy actions for "Map Without Territory"
  // The territory.id can be 'USE_MAP_NO_TERRITORY' string or NaN when converted to number
  const isMapWithoutTerritory =
    String(territory.id) === 'USE_MAP_NO_TERRITORY' ||
    isNaN(territory.id as number);

  return (
    <button
      key={territory.id}
      onClick={() => onSelect(territory)}
      className={`
        group w-full px-3 py-2 flex justify-between items-center gap-2 text-left text-sm
        hover:bg-neutral-200 hover:text-neutral-800 transition-all duration-300
        rounded-lg
        ${isSelected ? 'bg-sky-100 text-sky-900' : ''}
      `}
      data-testid={`territory-item-${territory.id}`}
      data-territory-id={territory.id}
    >
      <span className="flex-1 text-left flex items-center gap-1">
        <span
          className={`${
            territory.is_canonical ? 'text-sky-600 font-semibold' : ''
          }`}
        >
          {territory.name}
        </span>
        {territory.is_canonical && (
          <span className="text-neutral-500 whitespace-nowrap text-xs">
            (primary territory)
          </span>
        )}
      </span>

      <div className="flex items-center">
        {/* Pencil icon for editing (only shown when selected and not "Map Without Territory") */}
        {!isMapWithoutTerritory && (isMedScoutUser || canUserWrite) && (
          <div
            onClick={handleEdit}
            data-testid="territory-edit-button"
            className="opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer h-5 w-6 justify-center items-center flex"
          >
            <Icon icon={faPencil} className="w-3.5 h-3.5" />
          </div>
        )}

        {/* Copy icon (not shown for "Map Without Territory") */}
        {!isMapWithoutTerritory && (
          <div
            onClick={handleCopy}
            data-testid="territory-copy-button"
            className="opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer h-5 w-6 justify-center items-center flex"
          >
            <Icon icon={faCopy} className="w-4 h-4" />
          </div>
        )}

        {/* Check icon (only shown when selected) */}
        {isSelected && (
          <Icon icon={faCheck} className="w-4 h-4 pl-1 text-sky-600" />
        )}
      </div>
    </button>
  );
};
export default TerritoryPanelItem;
